import clsx from 'clsx'
import type { ReactNode } from 'react'
import type { GestureResponderEvent } from 'react-native'

import { Card, CardBody, CardFooter } from '@/src/elements/card'
import { TEST_ID } from '@/src/general/constants/testId'

import type { TPublicationApi } from '../../../api/types/getPublication.types'
import type { TMergePublicationTypes } from '../../../models/publication.types'
import { PublicationDetails, type TPublicationDetailsProps } from '../PublicationDetails'
import {
  PublicationShareCommentButtons,
  type TPublicationShareCommentButtonsProps,
} from '../PublicationShareCommentButtons'
import { PublicationCardHeader, type TPublicationCardHeaderProps } from './PublicationCardHeader'

export type TPublicationCardBaseProps = {
  className?: string
  footer?: ReactNode
  publication: TMergePublicationTypes<
    [
      TPublicationShareCommentButtonsProps['publication'],
      TPublicationDetailsProps['publication'],
      Pick<TPublicationApi, 'hasPrayed'>,
      TPublicationCardHeaderProps['publication'],
    ]
  >
} & Omit<TPublicationCardHeaderProps, 'publication'> &
  Record<'onCardPress' | 'onSharePress' | 'onCommentPress', (e?: GestureResponderEvent | React.MouseEvent) => void>

export const PublicationCardBase = (props: TPublicationCardBaseProps): ReactNode => {
  const { className, footer, publication, onCardPress, onSharePress, onCommentPress } = props
  const { hasPrayed } = publication
  const isScheduledPublication = publication.isScheduled && !publication.published
  const colors = hasPrayed ? 'success' : 'primary'

  return (
    <Card
      testID={
        isScheduledPublication
          ? TEST_ID.COMMUNITY.PUBLICATION_CARD.SCHEDULED
          : TEST_ID.COMMUNITY.PUBLICATION_CARD.PUBLISHED
      }
      className={clsx(hasPrayed ? 'bg-success-bg-high' : 'bg-default-bg-white', 'gap-4 p-4', className)}
      {...(!isScheduledPublication && { onPress: onCardPress, isPressable: true, additionalProps: { role: 'link' } })}
    >
      {!isScheduledPublication && <PublicationCardHeader {...props} />}

      <CardBody
        className={clsx(
          'overflow-hidden rounded-lg pt-4 px-2 pb-2',
          hasPrayed ? 'bg-success-bg-light' : 'bg-primary-bg-medium',
        )}
      >
        <PublicationDetails publication={publication} variant={colors} />
        {!isScheduledPublication && (
          <PublicationShareCommentButtons
            colors={colors}
            publication={publication}
            onSharePress={onSharePress}
            onCommentPress={onCommentPress}
          />
        )}
      </CardBody>
      {footer && <CardFooter className="p-0">{footer}</CardFooter>}
    </Card>
  )
}
